import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";

// ******************** Components  *******************
import { SidePopup } from "../../../components/model-popup/side-popup";
import { CustomButton } from "../../../components/buttons/button";
import TextIconInput from "../../../components/custom-fields/input";
import ErrorMessage from "../../../components/custom-fields/error-message";
import { AsyncPaginateSelect } from "../../../components/select/async-paginate-select";
import { toast } from "react-toastify";

// ******************** API & redux  *******************
import { useAppDispatch } from "../../../redux/hooks";
import { NotesErrorUpdate } from "../../../redux/slices/fire";
import { getPCCLocation } from "../../../api/list";  



// --- Option & API response shapes
interface FacilityOption {
  value: string;    // facilityId
  label: string;    // facilityName
  orgId: string;
  org_uuid: string;
}



    interface FacilityOption {
  value: string;    // facilityId
  label: string;    // facilityName
  orgId: string;
  org_uuid: string;
}

// -- Paginated loader
const loadFacilities: any = async (
  searchQuery: string,
  _loadedOptions: any,
  additional: any
) => {
  const perPage = 10;

  const params={
    search_value:searchQuery,
    page:additional.page,
    limit:perPage
  }
  const response = await getPCCLocation(params);
  const result = response?.result; // Access the result object from the response

  if (!result?.data) {
    return {
      options: [],
      hasMore: false,
      additional: { page: additional.page + 1 },
    };
  }

  const options: FacilityOption[] = result.data.map((f: any) => ({
    value: f.facId,  // Changed from facilityId to facId
    label: f.facilityName,
    orgId: f.orgId,
    org_uuid: f.org_uuid,
  }));

  return {
    options,
    hasMore: result.page < result.total_pages, // Use total_pages for pagination
    additional: { page: additional.page + 1 },
  };
};


// --- Workaround so SidePopup accepts children
const SidePopupAny = SidePopup as React.ComponentType<any>;

export const EmptyLocationErrorPopup = ({
  showModal,
  setShowModal,
  title,
  handleClose,
  poupDetails,
  afterChangeNotesList
}: any) => {
  const dispatch = useAppDispatch();
  const [initialFacility] = useState<FacilityOption | null>(
    poupDetails.facId
      ? {
          value: poupDetails.facId,
          label: poupDetails.facilityName,
          orgId: poupDetails.orgId,
          org_uuid: poupDetails.org_uuid,
        }
      : null
  );

  const formik = useFormik<{
    location: string;
    facility: FacilityOption | null;
  }>({
    enableReinitialize: true,
    initialValues: {
      location: poupDetails.location || "",
      facility: initialFacility,
    },
    validationSchema: yup.object({
      location: yup.string().required("Source location is required"),
      facility: yup.object<FacilityOption>().required("Please select a facility"),
    }),
    onSubmit: async (values) => {
      const payload = {
        location: values.location
      };


      await dispatch(NotesErrorUpdate({ id: poupDetails?.id, payload }))
        .unwrap()
        .then((res) => {
          if (res?.result) {
            toast.success("Source location updated successfully");
            setShowModal(false);
            afterChangeNotesList();
            formik.resetForm();
          } else {
            toast.error(res?.message ?? "Update failed");
          }
        })
        .catch((err) => {
          // err is either your rejectWithValue or a thrown network error
          toast.error(err?.message ?? err ?? "Failed to update source location");
        });
    } 
  });


  return (
    <SidePopupAny
      showModal={showModal}
      handleClose={handleClose}
      title={title}
      footerStyle="flex justify-end gap-[16px] "
      footerContent={
        <>
          <CustomButton
           buttonStyle="text-[#031227] bg-white hover:bg-[#F5F5F5] disabled:bg-white disabled:text-[#AAADB1]  px-[26px] py-[12px] text-[14px] font-semibold leading-[17px] border-[#E8ECF4] "
           buttonName="Cancel"
            primary={false}
            onClick={() => {
              setShowModal(false);
              formik.resetForm();
            }}
            type="button"
          />
          <CustomButton
            buttonName="Update"
            buttonStyle="text-white px-[36px] py-[12px] text-[14px] font-semibold leading-[17px] hover:bg-[#166B5E] disabled:bg-[#75C8BB]  "
            primary
            onClick={() => formik.handleSubmit()}
            type="submit"
            
          />
        </>
      }
    >
      <div className="space-y-4">
        {/* Source Location (read‑only) */}
        <div>
          <TextIconInput
            label="Source Location"
            name="source_location"
            value={formik.values.location}
            disable
          />
          {formik.touched.location && formik.errors.location && (
            <ErrorMessage>{formik.errors.location}</ErrorMessage>
          )}
        </div>

        {/* Facility Select (async & paginated) */}
        <div className="">
          <label className="font-medium text-[12px] text-[#1C2A3D] leading-[100%]">Facility Name <span className="text-red-500">*</span></label>

        <AsyncPaginateSelect
                  name="facility"
                  value={formik.values.facility}
                  loadOptions={loadFacilities}
                  additional={{ page: 1 }}
                  onChange={(opt: any) => {
                    formik.setFieldValue("facility", opt);
                    if (opt) {
                      formik.setFieldValue("location", opt.label);
                    }
                  }}
                  placeholder="Type to search facility"
                  isClearable
                  debounceTimeout={300}
                  className="w-[99%]"
                />
        </div>

        {/* Display the selected IDs */}
        {formik.values.facility && (
          <div className="space-y-4">
            <TextIconInput
              label="Facility ID"
              name="facility_id"
              value={formik.values.facility.value}
              disable
            />
            <TextIconInput
              label="Organization ID"
              name="org_id"
              value={formik.values.facility.orgId}
              disable
            />
          </div>
        )}
      </div>
    </SidePopupAny>
  );
};
